// Prd 승격 카드 = Catalog Entity 안 CI/CD tab 안 manual trigger cycle (5/27 검증 + sync retry fix)
import { useEffect, useState, useCallback, useRef } from 'react';
import { useEntity } from '@backstage/plugin-catalog-react';
import {
  useApi,
  identityApiRef,
  fetchApiRef,
} from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import {
  Card,
  CardContent,
  CardActions,
  Button,
  Typography,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
  Box,
  makeStyles,
  Tooltip,
} from '@material-ui/core';

const useStyles = makeStyles(theme => ({
  card: {
    marginBottom: theme.spacing(2),
  },
  tagRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(0.5, 0),
  },
  tagLabel: {
    fontWeight: 600,
    fontSize: '0.85rem',
    minWidth: 80,
  },
  tagValue: {
    fontFamily: 'monospace',
    fontSize: '0.8rem',
    wordBreak: 'break-all' as const,
  },
  statusChip: {
    fontWeight: 600,
  },
}));

const PROXY_BASE = '/api/proxy/gitlab/api/v4';
const PLATFORM_ADMIN_GROUP = 'group:default/platform-admins';
const DEPT_ADMIN_PATTERN = /^group:default\/[a-z0-9]+-hq$/;
const POLL_INTERVAL = 3000;
const MAX_POLLS = 100;

type Phase =
  | 'loading'
  | 'ready'
  | 'not-applicable'
  | 'confirming'
  | 'triggering'
  | 'running'
  | 'completed'
  | 'error';

interface PromoteState {
  phase: Phase;
  canPromote: boolean;
  devImage: string;
  prdImage: string;
  isDifferent: boolean;
  projectId: number | null;
  jobId: number | null;
  jobStatus: string;
  jobUrl: string;
  message: string;
}

const INITIAL_STATE: PromoteState = {
  phase: 'loading',
  canPromote: false,
  devImage: '',
  prdImage: '',
  isDifferent: false,
  projectId: null,
  jobId: null,
  jobStatus: '',
  jobUrl: '',
  message: '',
};

function extractImageTag(base64Content: string): string {
  try {
    const decoded = atob(base64Content);
    const match = decoded.match(/^\s*image:\s*(.+)$/m);
    return match ? match[1].trim() : '';
  } catch {
    return '';
  }
}

function shortTag(fullImage: string): string {
  const parts = fullImage.split(':');
  return parts.length > 1 ? parts[parts.length - 1] : fullImage;
}

export function PromotePrdCard() {
  const classes = useStyles();
  const { entity } = useEntity();
  const fetchApi = useApi(fetchApiRef);
  const identityApi = useApi(identityApiRef);
  const catalogApi = useApi(catalogApiRef);
  const [state, setState] = useState<PromoteState>(INITIAL_STATE);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollCountRef = useRef(0);

  const slug =
    entity.metadata.annotations?.['gitlab.com/project-slug'] ?? '';
  const entityNs = entity.metadata.namespace ?? 'default';
  const isDevEntity = entityNs.endsWith('-dev');


  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  const gitlabGet = useCallback(
    async (path: string) => {
      const resp = await fetchApi.fetch(`${PROXY_BASE}${path}`);
      if (!resp.ok) throw new Error(`GitLab API ${resp.status}`);
      return resp.json();
    },
    [fetchApi],
  );

  const gitlabPost = useCallback(
    async (path: string) => {
      const resp = await fetchApi.fetch(`${PROXY_BASE}${path}`, {
        method: 'POST',
      });
      if (!resp.ok) throw new Error(`GitLab API ${resp.status}`);
      return resp.json();
    },
    [fetchApi],
  );

  // Check user permission
  const resolveCanPromote = useCallback(async () => {
    try {
      const identity = await identityApi.getBackstageIdentity();
      const refs = new Set(
        (identity.ownershipEntityRefs ?? []).map(r => r.toLowerCase()),
      );
      if (refs.has(PLATFORM_ADMIN_GROUP)) return true;
      if (Array.from(refs).some(r => DEPT_ADMIN_PATTERN.test(r))) return true;

      // Catalog fallback 1: platform-admins 그룹의 children 확인
      try {
        const paGroup = await catalogApi.getEntityByRef(PLATFORM_ADMIN_GROUP);
        if (paGroup) {
          const children = (paGroup.spec?.children as string[]) ?? [];
          const childRefs = children.map(
            c => `group:default/${c.toLowerCase()}`,
          );
          if (childRefs.some(g => refs.has(g))) return true;
        }
      } catch {
        /* ignore */
      }

      // Catalog fallback 2: 유저 엔티티의 memberOf 관계 직접 조회
      const userRef = identity.userEntityRef;
      if (userRef) {
        try {
          const userEntity = await catalogApi.getEntityByRef(userRef);
          if (userEntity) {
            const memberOfRels = (userEntity.relations ?? [])
              .filter((r: any) => r.type === 'memberOf')
              .map((r: any) =>
                (typeof r.targetRef === 'string'
                  ? r.targetRef
                  : `${r.targetRef.kind}:${r.targetRef.namespace}/${r.targetRef.name}`
                ).toLowerCase(),
              );
            if (memberOfRels.includes(PLATFORM_ADMIN_GROUP)) return true;
            if (memberOfRels.some((r: string) => DEPT_ADMIN_PATTERN.test(r)))
              return true;
          }
        } catch {
          /* ignore */
        }
      }

      return false;
    } catch {
      return false;
    }
  }, [identityApi, catalogApi]);

  // Load all data on mount
  useEffect(() => {
    if (!slug) {
      setState(s => ({
        ...s,
        phase: 'not-applicable',
        message: 'gitlab.com/project-slug 어노테이션이 없습니다',
      }));
      return undefined;
    }
    if (isDevEntity) {
      setState(s => ({
        ...s,
        phase: 'not-applicable',
        message: 'Prd 배포 승인은 Prd 엔티티에서 관리합니다',
      }));
      return undefined;
    }

    let cancelled = false;
    (async () => {
      try {
        const canPromote = await resolveCanPromote();

        // 1. Resolve project ID
        const encodedSlug = encodeURIComponent(slug);
        const project = await gitlabGet(`/projects/${encodedSlug}`);
        const projectId = project.id as number;

        // 2. Fetch image tags from repo files
        let devImage = '';
        let prdImage = '';
        try {
          const devFile = await gitlabGet(
            `/projects/${projectId}/repository/files/${encodeURIComponent('k8s-dev/deployment.yaml')}?ref=main`,
          );
          devImage = extractImageTag(devFile.content);
        } catch {
          /* no dev deployment */
        }
        try {
          const prdFile = await gitlabGet(
            `/projects/${projectId}/repository/files/${encodeURIComponent('k8s/deployment.yaml')}?ref=main`,
          );
          prdImage = extractImageTag(prdFile.content);
        } catch {
          /* no prd deployment */
        }

        // 3. Find latest promote:prd job (API 1회, 파이프라인 종류 무관)
        let jobId: number | null = null;
        let jobStatus = '';
        let jobUrl = '';
        try {
          const recentJobs = await gitlabGet(
            `/projects/${projectId}/jobs?per_page=10`,
          );
          const promoteJob = (recentJobs as any[]).find(
            (j: any) => j.name === 'promote:prd',
          );
          if (promoteJob) {
            jobId = promoteJob.id;
            jobStatus = promoteJob.status;
            jobUrl = promoteJob.web_url ?? '';
          }
        } catch {
          /* jobs fetch failed */
        }

        // k8s 매니페스트도 없고 promote:prd 잡도 없으면 → 해당 없음
        if (!devImage && !prdImage && jobId === null) {
          if (!cancelled)
            setState(s => ({
              ...s,
              phase: 'not-applicable',
              message: '배포 가능한 서비스가 아닙니다 (k8s/ 매니페스트 없음)',
            }));
          return;
        }

        if (!cancelled) {
          // Helm 기반 (k8s 매니페스트 없지만 promote:prd 잡 존재)
          const isHelmBased = !devImage && !prdImage && jobId !== null;
          setState({
            phase: 'ready',
            canPromote,
            devImage,
            prdImage,
            isDifferent: isHelmBased ? true : devImage !== prdImage,
            projectId,
            jobId,
            jobStatus,
            jobUrl,
            message: isHelmBased
              ? 'Helm 차트 기반 배포 (이미지 태그는 ArgoCD에서 관리)'
              : '',
          });
        }
      } catch (err) {
        if (!cancelled) {
          setState(s => ({
            ...s,
            phase: 'error',
            message: `로드 실패: ${err}`,
          }));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug, isDevEntity, gitlabGet, resolveCanPromote]);

  // Poll job status
  const startPolling = useCallback(
    (projectId: number, jobId: number) => {
      pollCountRef.current = 0;
      pollRef.current = setInterval(async () => {
        pollCountRef.current += 1;
        if (pollCountRef.current > MAX_POLLS) {
          if (pollRef.current) clearInterval(pollRef.current);
          setState(s => ({
            ...s,
            phase: 'error',
            message: '폴링 타임아웃 (5분 초과)',
          }));
          return;
        }
        try {
          const job = await gitlabGet(
            `/projects/${projectId}/jobs/${jobId}`,
          );
          const status = job.status as string;
          if (['success', 'failed', 'canceled'].includes(status)) {
            if (pollRef.current) clearInterval(pollRef.current);
            setState(s => ({
              ...s,
              phase: 'completed',
              jobStatus: status,
              jobUrl: job.web_url ?? s.jobUrl,
            }));
          } else {
            setState(s => ({ ...s, jobStatus: status }));
          }
        } catch {
          /* polling error, continue */
        }
      }, POLL_INTERVAL);
    },
    [gitlabGet],
  );

  // Trigger promote:prd
  const handlePromote = useCallback(async () => {
    if (!state.projectId || !state.jobId) return;
    setState(s => ({ ...s, phase: 'triggering' }));
    try {
      const result = await gitlabPost(
        `/projects/${state.projectId}/jobs/${state.jobId}/play`,
      );
      setState(s => ({
        ...s,
        phase: 'running',
        jobStatus: result.status ?? 'pending',
        jobUrl: result.web_url ?? s.jobUrl,
      }));
      startPolling(state.projectId, state.jobId);
    } catch (err) {
      setState(s => ({
        ...s,
        phase: 'error',
        message: `트리거 실패: ${err}`,
      }));
    }
  }, [state.projectId, state.jobId, gitlabPost, startPolling]);

  // Render: not applicable
  if (state.phase === 'not-applicable') {
    return (
      <Card variant="outlined" className={classes.card}>
        <CardContent>
          <Typography variant="subtitle1" gutterBottom>
            Prd 배포 승인
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {state.message}
          </Typography>
        </CardContent>
      </Card>
    );
  }

  // Render: loading
  if (state.phase === 'loading') {
    return (
      <Card variant="outlined" className={classes.card}>
        <CardContent>
          <Box display="flex" alignItems="center" style={{ gap: 8 }}>
            <CircularProgress size={20} />
            <Typography variant="body2">배포 정보 조회 중...</Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  // Render: error
  if (state.phase === 'error') {
    return (
      <Card variant="outlined" className={classes.card}>
        <CardContent>
          <Typography variant="subtitle1" gutterBottom>
            Prd 배포 승인
          </Typography>
          <Typography variant="body2" color="error">
            {state.message}
          </Typography>
        </CardContent>
      </Card>
    );
  }

  // Status chip
  const statusColor = (status: string) => {
    switch (status) {
      case 'success':
        return { bg: '#4caf50', label: '성공' };
      case 'failed':
        return { bg: '#f44336', label: '실패' };
      case 'running':
        return { bg: '#2196f3', label: '실행 중' };
      case 'pending':
      case 'created':
        return { bg: '#ff9800', label: '대기 중' };
      case 'manual':
        return { bg: '#9e9e9e', label: '수동 대기' };
      case 'canceled':
        return { bg: '#757575', label: '취소됨' };
      default:
        return { bg: '#9e9e9e', label: status || '-' };
    }
  };

  const isRunning =
    state.phase === 'triggering' || state.phase === 'running';
  const isCompleted = state.phase === 'completed';
  const isHelmBased = !state.devImage && !state.prdImage && state.jobId !== null;
  const canClickPromote =
    state.phase === 'ready' &&
    state.canPromote &&
    state.isDifferent &&
    state.jobId !== null &&
    state.jobStatus === 'manual';

  const sc = statusColor(state.jobStatus);

  return (
    <>
      <Card variant="outlined" className={classes.card}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
            <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
              Prd 배포 승인
            </Typography>
            {state.jobStatus && (
              <Chip
                label={sc.label}
                size="small"
                className={classes.statusChip}
                style={{ backgroundColor: sc.bg, color: '#fff' }}
              />
            )}
          </Box>

          {isHelmBased ? (
            <Box>
              <Typography variant="body2" color="textSecondary">
                {state.message}
              </Typography>
            </Box>
          ) : (
            <>
              <Box className={classes.tagRow}>
                <Typography className={classes.tagLabel} color="textSecondary">
                  Dev
                </Typography>
                <Typography className={classes.tagValue}>
                  {shortTag(state.devImage) || '-'}
                </Typography>
              </Box>
              <Box className={classes.tagRow}>
                <Typography className={classes.tagLabel} color="textSecondary">
                  Prd
                </Typography>
                <Typography className={classes.tagValue}>
                  {shortTag(state.prdImage) || '-'}
                </Typography>
              </Box>

              {state.isDifferent ? (
                <Box mt={1}>
                  <Chip
                    label="승격 필요 (Dev ≠ Prd)"
                    size="small"
                    style={{ backgroundColor: '#ff9800', color: '#fff', fontWeight: 600 }}
                  />
                </Box>
              ) : state.devImage ? (
                <Box mt={1}>
                  <Chip
                    label="최신 버전 (Dev = Prd)"
                    size="small"
                    style={{ backgroundColor: '#4caf50', color: '#fff', fontWeight: 600 }}
                  />
                </Box>
              ) : null}
            </>
          )}

          {isRunning && (
            <Box display="flex" alignItems="center" mt={1}>
              <CircularProgress size={16} style={{ marginRight: 8 }} />
              <Typography variant="body2">
                {state.phase === 'triggering'
                  ? '잡 트리거 중...'
                  : `실행 중 (${state.jobStatus})`}
              </Typography>
            </Box>
          )}

          {isCompleted && state.jobUrl && (
            <Box mt={1}>
              <Typography variant="body2">
                <a
                  href={state.jobUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: '#1976d2' }}
                >
                  GitLab 잡 로그 보기 →
                </a>
              </Typography>
            </Box>
          )}
        </CardContent>

        <CardActions>
          {(() => {
            if (isRunning || isCompleted) return null;
            if (canClickPromote) {
              return (
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  onClick={() => setState(s => ({ ...s, phase: 'confirming' }))}
                >
                  Promote to Prd
                </Button>
              );
            }
            // Determine disable reason
            let disableReason = '';
            if (!state.canPromote) {
              disableReason = 'dept-admin 이상 권한이 필요합니다';
            } else if (!state.isDifferent) {
              disableReason = '이미 최신 버전입니다 (Dev = Prd)';
            } else if (state.jobId === null) {
              disableReason = 'promote:prd 잡을 찾을 수 없습니다';
            } else if (state.jobStatus !== 'manual') {
              disableReason = `잡 상태: ${state.jobStatus} (manual 상태에서만 실행 가능)`;
            }
            return (
              <Tooltip title={disableReason}>
                <span>
                  <Button variant="contained" size="small" disabled>
                    Promote to Prd
                  </Button>
                </span>
              </Tooltip>
            );
          })()}

          {state.jobUrl && !isRunning && (
            <Button
              size="small"
              href={state.jobUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              GitLab에서 보기
            </Button>
          )}
        </CardActions>
      </Card>

      {/* Confirmation dialog */}
      <Dialog
        open={state.phase === 'confirming'}
        onClose={() => setState(s => ({ ...s, phase: 'ready' }))}
      >
        <DialogTitle>Prd 배포 확인</DialogTitle>
        <DialogContent>
          {isHelmBased ? (
            <DialogContentText>
              최신 빌드를 Production에 승격합니다. ArgoCD가 자동으로 동기화합니다.
            </DialogContentText>
          ) : (
            <>
              <DialogContentText>
                다음 이미지를 Production에 배포합니다:
              </DialogContentText>
              <Box mt={1} p={1} bgcolor="#f5f5f5" borderRadius={4}>
                <Typography
                  variant="body2"
                  style={{ fontFamily: 'monospace', wordBreak: 'break-all' }}
                >
                  {state.devImage}
                </Typography>
              </Box>
              <DialogContentText style={{ marginTop: 12 }}>
                현재 Prd 이미지: <code>{shortTag(state.prdImage)}</code>
              </DialogContentText>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setState(s => ({ ...s, phase: 'ready' }))}>
            취소
          </Button>
          <Button onClick={handlePromote} color="primary" variant="contained">
            배포 승인
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
